/** Asked when a save changes auto-translated leaves (metaplan A6, AIE §8 "Unbind on direct
 *  edit"): per leaf, unbind it (the default — it keeps the edited text and stops following its
 *  origin) or keep the binding (the edit is replaced at the origin's next change). */
import {
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import type { TranslationBinding } from '@/api/tags';
import { AppDialog } from '@/components/AppDialog';
import { HelpLink } from '@/components/HelpLink';

type Choice = 'unbind' | 'keep';

interface Props {
  open: boolean;
  bindings: TranslationBinding[];
  saving?: boolean;
  onCancel: () => void;
  /** The ids to unbind. */
  onConfirm: (unbind: number[]) => void;
}

export function UnbindTranslationsDialog({ open, bindings, saving = false, onCancel, onConfirm }: Props) {
  const [choices, setChoices] = useState<Record<number, Choice>>({});
  useEffect(() => {
    if (open) setChoices(Object.fromEntries(bindings.map((b) => [b.id, 'unbind' as Choice])));
  }, [open, bindings]);

  return (
    <AppDialog
      open={open}
      onClose={onCancel}
      size="sm"
      title={
        <>
          You edited auto-translated text <HelpLink story="translation-glossary" newTab />
        </>
      }
      actions={
        <>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => onConfirm(bindings.filter((b) => choices[b.id] !== 'keep').map((b) => b.id))}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <Typography variant="body2" sx={{ mb: 2 }}>
        These leaves are machine translations of another leaf. Unbind them to keep your text for good,
        or keep the binding — then your edit is replaced the next time the origin changes.
      </Typography>
      <Stack spacing={2}>
        {bindings.map((b) => (
          <FormControl key={b.id}>
            <Typography variant="subtitle2">{b.target_label}</Typography>
            <Typography variant="caption" color="text.secondary">
              auto-translated from {b.origin_label}
            </Typography>
            <RadioGroup
              row
              value={choices[b.id] ?? 'unbind'}
              onChange={(e) => setChoices((prev) => ({ ...prev, [b.id]: e.target.value as Choice }))}
            >
              <FormControlLabel value="unbind" control={<Radio size="small" />} label="Unbind — keep my text" />
              <FormControlLabel value="keep" control={<Radio size="small" />} label="Keep the binding" />
            </RadioGroup>
          </FormControl>
        ))}
      </Stack>
    </AppDialog>
  );
}
